import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import {
  poundsToKilograms,
  stonesAndPoundsToKilograms,
  feetAndInchesToCentimeters,
} from '../../metricsConverter'
import { localizeLink } from '../../utils'
import { trackStartRegistration } from '../../analytics'

import SmileMeh from 'media/smile-meh.svg?svgr-webpack'
import SmileOkey from 'media/smile-okey.svg?svgr-webpack'
import Smile from 'media/smile.svg?svgr-webpack'
import useLocale from 'js/hooks/useLocale'

type SmileyProps = {
  id: 'smile-meh' | 'smile-okay' | undefined
  className: string
}

const Smiley = ({ id, className }: SmileyProps) => {
  switch (id) {
    case 'smile-meh':
      return <SmileMeh className={className} />
    case 'smile-okay':
      return <SmileOkey className={className} />

    default:
      return <Smile className={className} />
  }
}

const BMICalculator = () => {
  const { language } = useLocale()
  const metricWeightRef = useRef<HTMLInputElement>(null)
  const metricHeightRef = useRef<HTMLInputElement>(null)
  const imperialWeightRef = useRef<HTMLInputElement>(null)
  const imperialInchesRef = useRef<HTMLSelectElement>(null)
  const imperialFeetRef = useRef<HTMLSelectElement>(null)
  const britishStonesRef = useRef<HTMLSelectElement>(null)
  const britishPoundsRef = useRef<HTMLSelectElement>(null)
  const ctaRef = useRef<HTMLInputElement>(null)

  const [system, setSystem] = useState<'imperial' | 'british' | 'metric'>(
    'metric',
  )
  const [valid, setValid] = useState(false)
  const [metricWeight, setMetricWeight] = useState('')
  const [metricHeight, setMetricHeight] = useState('')
  const [imperialWeight, setImperialWeight] = useState('')
  const [imperialFeet, setImperialFeet] = useState('5')
  const [imperialInches, setImperialInches] = useState('0')
  const [britishStones, setBritishStones] = useState('10')
  const [britishPounds, setBritishPounds] = useState('0')

  const [result, setResult] = useState<number | false>(false)
  const [score, setScore] = useState<'good' | 'bad' | 'okay'>()
  const [scoreCategory, setScoreCategory] = useState<'low' | 'high' | false>(
    false,
  )
  const [smiley, setSmiley] = useState()
  const [scalePercentage, setScalePercentage] = useState<number>(0)

  useEffect(() => {
    const pageLang = language
    let locale = navigator.languages as
      | Array<'en-us' | 'en-gb' | string>
      | string

    if (pageLang === 'en' && locale) {
      if (locale[0]) {
        locale = locale[0].toLowerCase()
      }

      if (locale === 'en-us') {
        setSystem('imperial')
      } else if (locale === 'en-gb') {
        // UK uses stone and pounds (not only pounds as the US)
        setSystem('british')
      }
    }
  }, [language])

  const toggleSubmitButton = () => {
    let valid = true

    if (metricWeightRef.current) {
      if (metricWeightRef.current.value === '') {
        valid = false
      }
    }

    if (imperialWeightRef.current) {
      if (imperialWeightRef.current.value === '') {
        valid = false
      }
    }

    if (metricHeightRef.current) {
      if (metricHeightRef.current.value === '') {
        valid = false
      }
    }

    setValid(valid)
  }

  const setUnits = (event) => {
    event.preventDefault
    const system = event.currentTarget.getAttribute('data-value')
    setSystem(system)
    toggleSubmitButton()
  }

  const goBack = (event) => {
    event.preventDefault()
    setResult(false)
  }

  const controlInputs = (event) => {
    event.persist()

    switch (event.target) {
      case metricWeightRef.current:
        setMetricWeight(event.target.value)
        break
      case metricHeightRef.current:
        setMetricHeight(event.target.value)
        break
      case imperialWeightRef.current:
        setImperialWeight(event.target.value)
        break
      case imperialFeetRef.current:
        setImperialFeet(event.target.value)
        break
      case imperialInchesRef.current:
        setImperialInches(event.target.value)
        break
      case britishStonesRef.current:
        setBritishStones(event.target.value)
        break
      case britishPoundsRef.current:
        setBritishPounds(event.target.value)
        break
    }
  }

  const calculate = () => {
    let formula
    let result
    let score
    let scoreCategory: 'low' | 'high' | false = false
    let smiley

    const p = (value) => parseFloat(value.replace(/,/, /./))

    if (system === 'metric') {
      formula = (p(metricWeight) / (p(metricHeight) * p(metricHeight))) * 10_000
    } else if (system === 'imperial') {
      const feetAndInchesInCm = feetAndInchesToCentimeters(
        p(imperialFeet),
        p(imperialInches),
      )
      formula =
        (poundsToKilograms(p(imperialWeight)) /
          (feetAndInchesInCm * feetAndInchesInCm)) *
        10_000
    } else if (system === 'british') {
      formula =
        (stonesAndPoundsToKilograms(p(britishStones), p(britishPounds)) /
          (p(metricHeight) * p(metricHeight))) *
        10_000
    }

    result = Math.round(formula * 10) / 10
    score = 'bad'
    smiley = 'smile-meh'

    if (result >= 16 && result <= 30) {
      score = 'okay'
      smiley = 'smile-okay'
    }
    if (result >= 18.5 && result <= 25) {
      score = 'good'
      smiley = 'smile'
    }

    if (result < 16) {
      scoreCategory = 'low'
    } else if (result > 25) {
      scoreCategory = 'high'
    }

    setResult(result)
    setScore(score)
    setScoreCategory(scoreCategory)
    setSmiley(smiley)
    setScalePercentage(0)

    window.setTimeout(() => {
      let percentage = (result - 10) * (100 / 30)

      if (percentage > 100) {
        percentage = 100
      }
      if (percentage < 0) {
        percentage = 0
      }

      setScalePercentage(percentage)

      window.setTimeout(() => {
        ctaRef.current && ctaRef.current.classList.add('c-bmi__cta--appear')
      }, 1500)
    }, 100)
  }

  const submit = (event) => {
    event.preventDefault()
    calculate()
  }

  return (
    <div className="c-calculator  c-bmi">
      <h2 className="c-calculator__heading">BMI Calculator</h2>
      {!result && (
        <div className="c-calculator__inner">
          <form action="/" onSubmit={submit}>
            <div className="c-calculator__switcher">
              <button
                type="button"
                className={
                  system === 'metric'
                    ? 'c-calculator__switcher-button c-calculator__switcher-button--active'
                    : 'c-calculator__switcher-button'
                }
                onClick={setUnits}
                data-value="metric"
              >
                KG/CM
              </button>
              <button
                type="button"
                className={
                  system === 'imperial'
                    ? 'c-calculator__switcher-button c-calculator__switcher-button--active'
                    : 'c-calculator__switcher-button'
                }
                onClick={setUnits}
                data-value="imperial"
              >
                LBS/FT
              </button>
              <button
                type="button"
                className={
                  system === 'british'
                    ? 'c-calculator__switcher-button c-calculator__switcher-button--active'
                    : 'c-calculator__switcher-button'
                }
                onClick={setUnits}
                data-value="british"
              >
                ST/CM
              </button>
            </div>
            {system === 'metric' && (
              <div>
                <div className="c-bmi__input">
                  <label htmlFor="weight">Your weight (kg)</label>
                  <input
                    id="weight"
                    onKeyUp={toggleSubmitButton}
                    onChange={controlInputs}
                    value={metricWeight}
                    type="number"
                    step="0.1"
                    ref={metricWeightRef}
                  />
                </div>
                <div className="c-bmi__input">
                  <label htmlFor="height">Your height (cm)</label>
                  <input
                    id="height"
                    onKeyUp={toggleSubmitButton}
                    onChange={controlInputs}
                    value={metricHeight}
                    type="number"
                    ref={metricHeightRef}
                  />
                </div>
              </div>
            )}
            {system === 'imperial' && (
              <div>
                <div className="c-bmi__input">
                  <label htmlFor="weight">Your weight (lbs)</label>
                  <input
                    id="weight"
                    onKeyUp={toggleSubmitButton}
                    onChange={controlInputs}
                    value={imperialWeight}
                    type="number"
                    ref={imperialWeightRef}
                  />
                </div>
                <div className="c-bmi__input c-bmi__input--select">
                  <span className="c-bmi__fake-label">Your height</span>
                  <label className="sr-only" htmlFor="feet">
                    Feet
                  </label>
                  <select
                    id="feet"
                    onChange={controlInputs}
                    value={imperialFeet}
                    ref={imperialFeetRef}
                  >
                    <option value="4">4&prime;</option>
                    <option value="5">5&prime;</option>
                    <option value="6">6&prime;</option>
                  </select>
                </div>
                <div className="c-bmi__input c-bmi__input--select">
                  <label className="sr-only" htmlFor="inches">
                    Inches
                  </label>
                  <select
                    onChange={controlInputs}
                    value={imperialInches}
                    id="inches"
                    ref={imperialInchesRef}
                  >
                    <option value="0">0&Prime;</option>
                    <option value="1">1&Prime;</option>
                    <option value="2">2&Prime;</option>
                    <option value="3">3&Prime;</option>
                    <option value="4">4&Prime;</option>
                    <option value="5">5&Prime;</option>
                    <option value="6">6&Prime;</option>
                    <option value="7">7&Prime;</option>
                    <option value="8">8&Prime;</option>
                    <option value="9">9&Prime;</option>
                    <option value="10">10&Prime;</option>
                    <option value="11">11&Prime;</option>
                  </select>
                </div>
              </div>
            )}
            {system === 'british' && (
              <div>
                <div className="c-bmi__input c-bmi__input--select">
                  <span className="c-bmi__fake-label">Your weight</span>
                  <label className="sr-only" htmlFor="stones">
                    Stones
                  </label>
                  <select
                    onChange={controlInputs}
                    value={britishStones}
                    id="stones"
                    ref={britishStonesRef}
                  >
                    <option value="7">7 st</option>
                    <option value="8">8 st</option>
                    <option value="9">9 st</option>
                    <option value="10">10 st</option>
                    <option value="11">11 st</option>
                    <option value="12">12 st</option>
                    <option value="13">13 st</option>
                    <option value="14">14 st</option>
                    <option value="15">15 st</option>
                    <option value="16">16 st</option>
                    <option value="17">17 st</option>
                    <option value="18">18 st</option>
                    <option value="19">19 st</option>
                    <option value="20">20 st</option>
                    <option value="21">21 st</option>
                    <option value="22">22 st</option>
                    <option value="23">23 st</option>
                    <option value="24">24 st</option>
                    <option value="25">25 st</option>
                    <option value="26">26 st</option>
                    <option value="27">27 st</option>
                    <option value="28">28 st</option>
                    <option value="29">29 st</option>
                    <option value="30">30 st</option>
                    <option value="31">31 st</option>
                    <option value="32">32 st</option>
                    <option value="33">33 st</option>
                    <option value="34">34 st</option>
                    <option value="35">35 st</option>
                    <option value="36">36 st</option>
                    <option value="37">37 st</option>
                    <option value="38">38 st</option>
                  </select>
                </div>
                <div className="c-bmi__input c-bmi__input--select">
                  <label className="sr-only" htmlFor="pounds">
                    Pounds
                  </label>
                  <select
                    onChange={controlInputs}
                    value={britishPounds}
                    id="pounds"
                    ref={britishPoundsRef}
                  >
                    <option value="0">0 lb</option>
                    <option value="1">1 lb</option>
                    <option value="2">2 lb</option>
                    <option value="3">3 lb</option>
                    <option value="4">4 lb</option>
                    <option value="5">5 lb</option>
                    <option value="6">6 lb</option>
                    <option value="7">7 lb</option>
                    <option value="8">8 lb</option>
                    <option value="9">9 lb</option>
                    <option value="10">10 lb</option>
                    <option value="11">11 lb</option>
                  </select>
                </div>
                <div className="c-bmi__input">
                  <label htmlFor="weight">Your height (cm)</label>
                  <input
                    id="weight"
                    onKeyUp={toggleSubmitButton}
                    onChange={controlInputs}
                    value={metricHeight}
                    type="number"
                    ref={metricHeightRef}
                  />
                </div>
              </div>
            )}
            <button
              className="c-button c-button--primary  c-calculator__submit"
              type="submit"
              disabled={valid ? undefined : true}
            >
              <span>Calculate BMI</span>
            </button>
          </form>
        </div>
      )}
      {result && (
        <div className="c-calculator__inner">
          <div
            className={cn('c-bmi__result', {
              'c-bmi__result--bad': score === 'bad',
              'c-bmi__result--okay': score === 'okay',
              'c-bmi__result--good': score === 'good',
            })}
          >
            <button className="c-calculator__back" onClick={goBack}>
              <span className="sr-only">Close result</span>
            </button>
            <h3 className="c-bmi__result-heading">Your BMI</h3>
            <p className="c-bmi__result-number">
              {result}
              <Smiley className="c-bmi__result-smiley" id={smiley} />
            </p>
            <p className="c-bmi__result-text">
              {score === 'bad' && scoreCategory === 'low' && (
                <span>
                  Your BMI is too low. Please consider making some changes to
                  your diet and exercise routine to get your BMI up, it will
                  make you healthier.
                </span>
              )}
              {score === 'bad' && scoreCategory === 'high' && (
                <span>
                  From a BMI standpoint, you would benefit from making a few
                  changes to your diet and exercise routine.
                </span>
              )}
              {score === 'okay' && (
                <span>
                  You are very close to what is considered a healthy BMI.
                </span>
              )}
              {score === 'good' && (
                <span>
                  Awesome! From a BMI standpoint, you’re in good shape.
                </span>
              )}
            </p>
            <div className="c-bmi__scale" aria-hidden="true">
              <span className="c-bmi__scale-low">10</span>
              <span className="c-bmi__scale-high">40</span>
              <span className="c-bmi__healthy-range">
                <span>18.5–25</span>
              </span>

              <div
                className="c-bmi__scale-marker"
                style={{ left: `${scalePercentage}%` }}
              />
            </div>
            <div className="c-bmi__cta" ref={ctaRef}>
              <p>Reach your personal health goal!</p>
              <a
                className="c-button c-button--primary"
                href={localizeLink('/signup/', language)}
                onClick={() => {
                  trackStartRegistration()
                }}
              >
                <span>Sign up for Lifesum</span>
              </a>
            </div>
            <small className="c-bmi__fine-print">
              Generally speaking, a Body Mass Index of 18.5 to 25 is considered
              healthy for an adult. Depending on your muscle mass, genes and age
              it may not apply to you.
            </small>
          </div>
        </div>
      )}
    </div>
  )
}

export default BMICalculator
